import { AwsUi } from '../../../core/runtime/src';
import BaseInput from '../../input/src/base-input';
import MaskedInputController from './controllers/masked-input-controller';
function getFormat(format, use24Hour) {
    var segments = [use24Hour ? { min: 0, max: 23, length: 2 } : { min: 1, max: 12, length: 2 }];
    if (format.match('hh:mm')) {
        segments.push({ min: 0, max: 59, length: 2 });
    }
    if (format.match('hh:mm:ss')) {
        segments.push({ min: 0, max: 59, length: 2 });
    }
    return {
        separator: ':',
        segments: segments
    };
}
export var TimeInput = AwsUi.__addComponent('awsui-time-input', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            var _this = this;
            this.__controller = new MaskedInputController({
                getValue: function () { return _this.value; },
                isReadOnly: function () { return _this.readonly; },
                isDisabled: function () { return _this.disabled; },
                notifyInput: function (value) {
                    _this.value = value;
                    _this.__fireEvent('input', { value: value });
                },
                notifyChange: function (value) {
                    _this.value = value;
                    _this.__fireEvent('change', { value: value });
                },
                overrideValue: function (value) {
                    _this.value = value;
                },
                focus: function () { return _this.focus(); },
                notifyFocus: function () { return _this.__fireEvent('focus'); },
                notifyBlur: function () { return _this.__fireEvent('blur'); },
                notifyKeyEvent: function () { return null; }
            }, this.node);
            this.__controller.setFormat(getFormat(this.format, this.use24Hour));
            if (!this.controlId) {
                this.controlId = this.__controller.generatedId;
            }
        },
        attached: function () {
            this.__controller.addListeners();
        },
        detached: function () {
            this.__controller.removeListeners();
        }
    },
    properties: {
        // Text entered into the form element.
        value: {
            type: 'string',
            defaultValue: '',
            onChange: function (oldValue, newValue) {
                var updated = this.__controller.maskedValueAttributeChangeHandler(oldValue, newValue);
                if (updated !== newValue) {
                    this.value = updated;
                }
            }
        },
        // The name of the control used in HTML forms.
        name: {
            type: 'string'
        },
        // Specifies the granularity of the time input.
        format: {
            type: 'string',
            defaultValue: 'hh:mm:ss',
            valid: ['hh', 'hh:mm', 'hh:mm:ss'],
            onChange: function (oldValue, newValue) {
                var updated = this.__controller.reformat(getFormat(newValue, this.use24Hour), this.value);
                if (updated !== this.value) {
                    this.value = updated;
                }
            }
        },
        // Specifies whether the component should use 12 hour or 24 hour format.
        // Note: When using 12 hour format, there is no option for picking 'am' or 'pm'.
        use24Hour: {
            type: 'boolean',
            defaultValue: true,
            onChange: function (oldValue, newValue) {
                var updated = this.__controller.reformat(getFormat(this.format, newValue), this.value);
                if (updated !== this.value) {
                    this.value = updated;
                }
            }
        },
        // Placeholder text rendered when the value is an empty string.
        placeholder: {
            type: 'string'
        },
        // Specifies that the input should be disabled, preventing the user from
        // modifying the value and preventing the value from being included in a
        // form submission. A disabled input cannot receive focus.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // Specifies that the input should be readonly, preventing the user from
        // modifying the value but including it in a form submission. A readonly input can receive focus.
        //
        // Do not use readonly inputs outside of a form.
        readonly: {
            type: 'boolean'
        },
        // Overrides invalidation state. Usually invalid state comes from wrapping `awsui-form-field` component, however
        // sometimes you need to override its state when you have more than one input within single form-field.
        invalid: {
            type: 'boolean',
            nullable: true
        },
        // Allows you to indicate that the control is to be focused as
        // soon as the `load` event triggers, allowing the user to just start
        // typing without having to manually focus the input. Don't use this option
        // in pages that allow for the field to be scrolled out of the viewport.
        autofocus: {
            type: 'boolean'
        },
        // Specifies whether browsers should enable autocomplete for this input.
        // In some cases it may be appropriate to disable autocomplete, for example for
        // security-sensitive fields.
        autocomplete: {
            type: 'boolean',
            defaultValue: true
        },
        // Id of the internal input. Useful for relating a label element's
        // "for" attribute to this control. It defaults to an automatically generated id.
        controlId: {
            type: 'string',
            announceValue: true,
            onChange: function () {
                if (!this.controlId) {
                    this.controlId = this.__controller.generatedId;
                }
            }
        },
        // Adds an aria label on the native input.
        label: {
            type: 'string'
        },
        // Adds aria-labelledby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define a custom id inside the label
        // 2. Refer to that label from every single control under that label using this property
        ariaLabelledby: {
            type: 'string'
        },
        // Adds aria-describedby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string',
            nullable: true
        },
        // Adds aria-required on the native input
        ariaRequired: {
            type: 'boolean'
        },
        // A default suggested aria-describedby value that can be pushed down from a FormField
        __suggestedAriaDescribedby: {
            type: 'string'
        },
        __confirmedValue: {
            type: 'string',
            defaultValue: ''
        }
    },
    functions: {
        // Sets input focus onto the UI control.
        focus: function () {
            this.node.querySelector('input').focus();
        }
    },
    events: {
        // Fired when input focus is set onto the UI control.
        focus: {
            bubbles: true,
            cancelable: false
        },
        // Fired when input focus is removed from the UI control.
        blur: {
            bubbles: true,
            cancelable: false
        },
        // Mimics the native `change` event on inputs.
        // The event `detail` contains the current value of the field.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'TimeInput.ChangeDetail'
        },
        // Mimics the native `input` event on inputs.
        // The event `detail` contains the current value of the field.
        input: {
            bubbles: true,
            cancelable: false,
            detailType: 'TimeInput.ChangeDetail'
        }
    },
    render: function (m, h) {
        return (AwsUi.__createElement(BaseInput, { value: this.value, type: this.type, autocomplete: this.autocomplete, disableBrowserAutocorrect: true, disabled: this.disabled, readonly: this.readonly, "aria-invalid": this.invalid, "aria-required": this.ariaRequired, additionalAttrs: {
                name: this.name,
                id: this.controlId || null,
                placeholder: this.placeholder,
                autofocus: this.autofocus,
                'aria-describedby': this.ariaDescribedby || this.__suggestedAriaDescribedby || undefined,
                'aria-label': this.label,
                'aria-labelledby': this.ariaLabelledby
            } }));
    }
});
//# sourceMappingURL=time-input.js.map